import React, { useState, useEffect, useRef } from "react";
import ScrollMagic from "scrollmagic";
import HandDrawnBracket from "../atoms/svgs/HandDrawnBracket";

// Helper function to convert Tailwind color classes to hex values
const getColorValue = (colorClass: string): string => {
	const colorMap: Record<string, string> = {
		blue: "#6C84FA",
		green: "#73BFB8",
		red: "#E9AFA3",
		yellow: "#FFB115",
	};

	// Extract base color name
	const baseColor = colorClass.replace("bg-", "").replace("border-", "");
	return colorMap[baseColor] || "#6C84FA"; // Default to blue
};

export interface ProcessStep {
	label: string;
	barColor: string;
	bracketColor: string; // Tailwind color class or hex value
	startPhase: number; // 0-4 (Discovery, Design, Build, Launch, Post-Launch)
	startOffset: number; // 0-1 (position within the phase)
	duration: number; // number of phases the bar spans
	showRightBracket?: boolean; // If false, bar fades to the right instead of showing a bracket
	hoverText?: string; // Text to display when this step is hovered
}

interface HowWeWorkProps {
	title?: string;
	subtitle?: string;
	bgColor?: string;
	processSteps?: ProcessStep[];
}

const timelinePhases = ["Discovery", "Design", "Build", "Launch", "Post-Launch"];

export const defaultProcessSteps: ProcessStep[] = [
	{
		label: "Pre-Sales Support",
		barColor: "bg-blue/10",
		bracketColor: "blue",
		startPhase: 0, // Discovery
		startOffset: 0.1,
		duration: 1.5, // Spans Discovery and half of Design
		hoverText: "We help scope, estimate, and win the deal.",
	},
	{
		label: "Design Handoff",
		barColor: "bg-green/10",
		bracketColor: "green",
		startPhase: 1, // Design
		startOffset: 0.4, // Midway through Design
		duration: 1.25, // Spans half of Design and half of Build
		hoverText: "You send the files. We ask smart questions.",
	},
	{
		label: "Ticketed Build Process",
		barColor: "bg-red/10",
		bracketColor: "red",
		startPhase: 2, // Build
		startOffset: 0.2, // Early in Build
		duration: 1.8, // Spans most of Build and into Launch
		hoverText: "Every block becomes a deliverable.",
	},
	{
		label: "Q&A Feedback Loops",
		barColor: "bg-yellow/10",
		bracketColor: "yellow",
		startPhase: 2, // Build
		startOffset: 0.7, // Late in Build
		duration: 0.9, // Short bar, spans late Build and early Launch
		hoverText: "We test before your client sees a thing.",
	},
	{
		label: "Launch & Support",
		barColor: "bg-blue/10",
		bracketColor: "blue",
		startPhase: 3, // Launch
		startOffset: 0.5, // Early in Launch
		duration: 2.3, // Longest bar, spans Launch and Post-Launch
		showRightBracket: false,
		hoverText: "Go live with confidence - we're behind you all the way.",
	},
];

const MOBILE_BREAKPOINT = 768;

const HowWeWork: React.FC<HowWeWorkProps> = ({
	title = "A Seamless Extension of Your Agency",
	subtitle = "Hover to see how we work:",
	bgColor = "bg-white",
	processSteps = defaultProcessSteps,
}) => {
	const [hoveredStepIndex, setHoveredStepIndex] = useState<number | null>(null);
	const [selectedStepIndex, setSelectedStepIndex] = useState<number>(0); // mobile tap-to-select, default first step
	const [isMobile, setIsMobile] = useState<boolean>(false);
	const sectionRef = useRef<HTMLDivElement>(null);
	const gridLineRef = useRef<HTMLDivElement>(null);
	const controllerRef = useRef<any>(null);

	// Match mobile breakpoint so CTA and subtitle behave correctly on touch
	useEffect(() => {
		const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
		const update = () => setIsMobile(mql.matches);
		update();
		mql.addEventListener("change", update);
		return () => mql.removeEventListener("change", update);
	}, []);

	// Calculate bar position and width as percentages
	const getBarStyle = (step: ProcessStep, animatedWidth = true) => {
		const phaseWidth = 100 / timelinePhases.length; // 20% per phase
		const leftPercent = step.startPhase * phaseWidth + step.startOffset * phaseWidth;
		const widthPercent = step.duration * phaseWidth;
		return {
			left: `${leftPercent}%`,
			width: animatedWidth ? "0%" : `${widthPercent}%`, // Start at 0 for animation
			finalWidth: `${widthPercent}%`, // Store final width
		};
	};

	const hoveredStep = hoveredStepIndex !== null ? processSteps[hoveredStepIndex] : null;
	// On mobile: always show a step (default first); on desktop: show only when hovered
	const activeStep = isMobile
		? processSteps[selectedStepIndex]
		: hoveredStep;
	const showDotForIndex = (index: number) =>
		isMobile ? selectedStepIndex === index : hoveredStepIndex === index;

	// Set up ScrollMagic animations
	useEffect(() => {
		if (!sectionRef.current) return;

		// Ensure we're on the client side
		if (typeof window === "undefined" || !window.document) return;

		// Dynamically import ScrollMagic only on client side
		import("scrollmagic")
			.then((ScrollMagic) => {
				// Initialize ScrollMagic controller
				const controller = new ScrollMagic.default.Controller();
				controllerRef.current = controller;

				// Get all elements
				const gridLine = gridLineRef.current;
				const labelElements = sectionRef.current.querySelectorAll<HTMLElement>(".process-label");
				const barElements = sectionRef.current.querySelectorAll<HTMLElement>(".barItem");

				// Animate vertical grid line - draw from bottom left
				if (gridLine) {
					const lineScene = new ScrollMagic.default.Scene({
						triggerElement: sectionRef.current,
						triggerHook: 0.8,
					})
						.on("enter", () => {
							gridLine.style.transformOrigin = "bottom left";
							gridLine.style.transition = "height 0.8s ease-out";

							// Trigger animation immediately
							setTimeout(() => {
								gridLine.style.height = "100%";
							}, 50);
						})
						.addTo(controller);
				}

				// Animate labels - opacity 0→1, translateY -15→0
				labelElements.forEach((label, index) => {
					const labelScene = new ScrollMagic.default.Scene({
						triggerElement: sectionRef.current,
						triggerHook: 0.8,
					})
						.on("enter", () => {
							label.style.opacity = "0";
							label.style.transform = "translateY(-15px)";
							label.style.transition = "opacity 0.6s ease-out, transform 0.6s ease-out";

							// Trigger animation with stagger delay
							setTimeout(() => {
								label.style.opacity = "1";
								label.style.transform = "translateY(0)";
							}, index * 100); // Stagger labels by 100ms each
						})
						.addTo(controller);
				});

				// Animate bars - width 0 to final width, with delay after labels complete
				barElements.forEach((bar, index) => {
					const finalWidth = bar.getAttribute("data-final-width") || "0%";
					const barScene = new ScrollMagic.default.Scene({
						triggerElement: sectionRef.current,
						triggerHook: 0.8,
					})
						.on("enter", () => {
							bar.style.width = "0%";
							bar.style.transition = "width 1s ease-out";
							bar.style.opacity = "0";

							// Calculate delay: labels animation (600ms) + label stagger + additional delay + bar stagger
							const labelAnimationDuration = 600; // Label transition duration in ms
							const totalLabelStagger = (labelElements.length - 1) * 100; // Total stagger for all labels
							const delayAfterLabels = 0; // Additional delay after labels complete
							const barStagger = index * 100; // Stagger between bars
							const totalDelay =
								labelAnimationDuration + totalLabelStagger + delayAfterLabels + barStagger;

							setTimeout(() => {
								bar.style.width = finalWidth;
								bar.style.opacity = "1";
							}, totalDelay);
						})
						.addTo(controller);
				});
			})
			.catch((err) => {
				console.error("Failed to load ScrollMagic:", err);
			});

		// Cleanup
		return () => {
			if (controllerRef.current) {
				controllerRef.current.destroy(true);
			}
		};
	}, [processSteps]);

	return (
		<div className={`${bgColor} py-[clamp(40px,6.25vw,80px)] px-0 md:px-2 xl:px-0`}>
			<div
				ref={sectionRef}
				className="howWeWorkSection container mx-auto max-w-6xl px-0 md:px-4"
			>
				{/* Title and subtitle: padding on mobile so they don’t sit on the edge */}
				<div className="px-4 md:px-0">
					{title && <h3 className="font-bold leading-tight mb-2">{title}</h3>}
					{(isMobile ? "Tap to see how we work:" : subtitle) && (
						<p className="text-[clamp(18px,1.67vw,24px)] text-gray-700 mb-12">
							{isMobile ? "Tap to see how we work:" : subtitle}
						</p>
					)}
				</div>

				{/* Gantt Chart Container — left padding on mobile so dot/labels don’t touch edge; lines still full width */}
				<div className="relative pl-4 md:pl-0">
					{/* Vertical line that extends from top through separator - positioned at container level */}
					<div
						ref={gridLineRef}
						className="absolute bottom-0 w-px bg-gray-200 left-40 md:left-56 pointer-events-none"
						style={{ height: "0%", top: 0 }}
					/>

					{/* Process Steps and Bars with Grid Lines */}
					<div className="relative mb-4">
						{/* Grid Lines - positioned within process steps area */}
						<div className="absolute top-0 left-0 right-0 h-full pointer-events-none">
							{/* Top horizontal line - extends full width */}
							<div className="absolute top-0 left-0 right-0 h-px bg-gray-200" />
						</div>

						{/* Process Step Rows */}
						{processSteps.map((step, index) => {
							const barStyle = getBarStyle(step, false); // Get final width for data attribute
							return (
								<div
									key={index}
									className="relative cursor-pointer"
									onMouseEnter={() => setHoveredStepIndex(index)}
									onMouseLeave={() => setHoveredStepIndex(null)}
									onClick={() => isMobile && setSelectedStepIndex(index)}
									onKeyDown={(e) =>
										isMobile && (e.key === "Enter" || e.key === " ") && setSelectedStepIndex(index)
									}
									role={isMobile ? "button" : undefined}
									tabIndex={isMobile ? 0 : undefined}
								>
									{/* Process Step Label */}
									<div className="flex items-center py-1">
										<div className="w-40 md:w-56 flex-shrink-0 pr-8 md:pr-4 text-right flex justify-end items-center gap-1.5 md:gap-2 min-w-0">
											{showDotForIndex(index) && (
												<span
													className="hoveredStepDot w-[6px] h-[6px] rounded-full flex-shrink-0"
													style={{
														backgroundColor: getColorValue(step.bracketColor),
													}}
												></span>
											)}
											<span
												className="process-label text-[11px] md:text-[clamp(14px,1.11vw,16px)] font-normal leading-tight md:leading-normal text-right whitespace-nowrap min-w-0"
												style={{ opacity: 0 }}
											>
												{step.label}
											</span>
										</div>
										{/* Bar Container */}
										<div
											className="flex-1 relative h-12"
											style={{ overflow: step.showRightBracket === false ? "visible" : "hidden" }}
										>
											{/* Gantt Bar with bracketed styling */}
											<div
												className={`barItem absolute top-2 h-8 ${step.barColor}`}
												data-final-width={
													step.showRightBracket === false
														? `calc(${barStyle.width} + 200px)`
														: barStyle.width
												}
												style={{
													left: barStyle.left,
													width: "0%", // Start at 0 for animation
													...(step.showRightBracket === false && {
														maskImage: "linear-gradient(to right, black 60%, transparent 100%)",
														WebkitMaskImage:
															"linear-gradient(to right, black 60%, transparent 100%)",
													}),
												}}
											>
												{/* Left bracket - hand-drawn SVG */}
												<HandDrawnBracket
													color={step.bracketColor}
													side="left"
													height={32}
													className="absolute"
												/>
												{/* Right bracket - hand-drawn SVG (conditional) */}
												{step.showRightBracket !== false && (
													<HandDrawnBracket
														color={step.bracketColor}
														side="right"
														height={32}
														className="absolute"
													/>
												)}
											</div>
										</div>
									</div>
								</div>
							);
						})}
					</div>

					{/* Horizontal separator line - extends full width including below row labels */}
					<div className="border-t border-gray-200 mb-4" />

					{/* Timeline Phases Labels — tighter spacing on mobile to free room for left column */}
					<div className="flex relative ml-40 md:ml-56">
						{timelinePhases.map((phase, index) => (
							<div
								key={index}
								className="flex-1 text-center text-[9px] md:text-[clamp(12px,.97vw,14px)] font-bold text-black pl-0 md:pl-2 min-w-0"
							>
								{phase}
							</div>
						))}
					</div>
				</div>

				{/* CTA: on mobile always show active (selected) step; on desktop show when hovered; padding on mobile */}
				<div className="mt-12 flex items-center justify-start min-h-[60px] px-4 md:px-0">
					{activeStep && activeStep.hoverText ? (
						<div className="relative inline-flex items-center">
							{/* Left bracket */}
							<div
								className="relative flex-shrink-0"
								style={{ width: "20px", height: "60px", marginRight: "8px" }}
							>
								<HandDrawnBracket
									color={activeStep.bracketColor}
									side="left"
									height={60}
								/>
							</div>
							{/* Text */}
							<span className="text-2xl md:text-4xl text-[clamp(20px,2.5vw,36px)] font-bold leading-tight text-black">
								{activeStep.hoverText}
							</span>
							{/* Right bracket */}
							<div
								className="relative flex-shrink-0"
								style={{ width: "20px", height: "60px", marginLeft: "8px" }}
							>
								<HandDrawnBracket
									color={activeStep.bracketColor}
									side="right"
									height={60}
								/>
							</div>
						</div>
					) : (
						/* Placeholder to maintain space when nothing is hovered (desktop only) */
						<div className="invisible text-2xl md:text-4xl text-[clamp(20px,2.5vw,36px)] font-bold leading-tight">
							&nbsp;
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default HowWeWork;
